'use client';

import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type {
  UfdrArtifactType,
  UfdrArtifactView,
  UfdrExportData,
  UfdrQuery,
  UfdrSection,
  UfdrTableModel,
} from '@/shared/ufdr/legacy-types';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import type {
  MobileFrontEndBubbleMessageView,
  MobileUiConfigArtifact,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { buildArtifactListQuery } from '@/shared/mobile-service/lib/build-query';
import { resolveArtifactColumns } from '@/shared/mobile-service/lib/columns';
import { artifactBubbleView } from '@/shared/mobile-service/lib/labels';
import { buildOrderBy, compileWhere } from '@/shared/mobile-service/lib/where';
import { withChatIds } from '@/shared/ufdr-export-adapter';
import { useAcquisitionDeviceOwners } from '@/shared/mobile-service/hooks/useAcquisitionDeviceOwners';
import { useAcquisitionSummary } from '@/shared/mobile-service/hooks/useAcquisitions';
import { useArtifactList } from '@/shared/mobile-service/hooks/useArtifactList';
import { useMessageThreads } from '@/shared/mobile-service/hooks/useMessageThreads';
import { useMobileUiConfig } from '@/shared/mobile-service/hooks/useMobileUiConfig';
import { useThreadMessages } from '@/shared/mobile-service/hooks/useThreadMessages';
import { useTimelineEvents } from '@/shared/mobile-service/hooks/useTimelineEvents';
import {
  attachChatMessages,
  contactArtifacts,
  mapAcquisitionStats,
  mapContactRows,
  mapMappedMessages,
  mapMobileTimeline,
  mapThreadsToChats,
  messageArtifacts,
  preferByCount,
  openChatIdFromRoute,
  splitUfdrChatId,
} from '@/shared/ufdr-mobile-adapter';
import { useMobileForensicAcquisition } from './mobile-forensic/useMobileForensicAcquisition';
import { useMobileForensicWorkspace } from './mobile-forensic/useMobileForensicWorkspace';

const VIEWS: UfdrArtifactView[] = ['table', 'messages', 'map', 'gallery', 'cards', 'timeline'];

const IDLE_ARTIFACT = {
  graphql_type: 'Idle',
  endpoint: 'mobile',
  fields: [],
} as MobileUiConfigArtifact;

const IDLE_CONFIG = { artifacts: {} } as MobileUiConfigResponse;

const IDLE_MESSAGE_VIEW: MobileFrontEndBubbleMessageView = {
  name: 'messages',
  body_field: 'body',
  timestamp_field: 'timestamp',
  thread: { mode: 'by_field', key_field: 'id' },
};

type State = {
  artifactType?: string;
  view?: UfdrArtifactView;
  query: UfdrQuery;
};

type Action =
  | { type: 'artifact'; artifactType: string }
  | { type: 'view'; view: UfdrArtifactView }
  | { type: 'query'; query: UfdrQuery }
  | { type: 'page'; page: number }
  | { type: 'focus'; artifactType?: string; view?: UfdrArtifactView };

function reducer(state: State, action: Action): State {
  if (action.type === 'artifact') {
    return { artifactType: action.artifactType, view: undefined, query: { ...state.query, page: 1 } };
  }
  if (action.type === 'view') return { ...state, view: action.view };
  if (action.type === 'focus') {
    return {
      ...state,
      artifactType: action.artifactType ?? state.artifactType,
      view: action.view ?? state.view,
    };
  }
  if (action.type === 'page') {
    if (state.query.page === action.page) return state;
    return { ...state, query: { ...state.query, page: action.page } };
  }
  return { ...state, query: action.query };
}

/** Page number from a `/mobile-viewer` `page` param. Missing or invalid stays unset. */
export function pageFromBrowseParam(page: string | undefined): number | undefined {
  if (!page) return undefined;
  const value = Number(page);
  if (!Number.isInteger(value) || value < 1) return undefined;
  return value;
}

function artifactViews(artifact: MobileUiConfigArtifact): UfdrArtifactView[] {
  const named = (artifact.front_end?.views ?? [])
    .map((view) => view.name)
    .filter((name): name is UfdrArtifactView => VIEWS.includes(name as UfdrArtifactView));
  return named.includes('table') ? named : [...named, 'table'];
}

/**
 * Mobile-service feed for MobileForensicViewer. With the flag off, or when no acquisition
 * matches the locker path, the caller keeps the sidecar export.
 */
export function useMobileForensicViewerData(args: {
  enabled: boolean;
  path?: string | null;
  caseFolderName?: string | null;
  acquisitionId?: number | null;
  artifactType?: string;
  view?: string;
  rowIds?: string[];
  /** Citation and browse links. Raw thread key, or the composite chat id. */
  threadId?: string;
  /** Browse `f` param. Applied to the artifact query, not only stored on the context. */
  filters?: string;
  /** Browse `q` param. Restores the workspace search on a shared link. */
  search?: string;
  /** Browse `page` param. 1-based. */
  page?: string;
  section?: UfdrSection;
  sidecar?: UfdrExportData;
}) {
  const t = useTimePilotT();
  const session = useCellServiceSession();
  const mobileOn = args.enabled && session.enabled;
  const acquisition = useMobileForensicAcquisition({
    enabled: mobileOn,
    path: args.path,
    caseFolderName: args.caseFolderName,
    acquisitionId: args.acquisitionId,
  });
  const matched = mobileOn && acquisition.acquisitionId != null;
  const acquisitionId = acquisition.acquisitionId;

  const uiConfig = useMobileUiConfig(matched);
  const owners = useAcquisitionDeviceOwners(matched && acquisitionId != null ? [acquisitionId] : []);
  const summary = useAcquisitionSummary(acquisitionId ?? 0, matched);

  const artifactTypes = useMemo<UfdrArtifactType[]>(() => {
    const artifacts = uiConfig.data?.artifacts;
    if (!artifacts) return [];
    return Object.values(artifacts).map((artifact) => ({
      id: artifact.graphql_type,
      label: artifact.title || artifact.graphql_type,
      family: artifact.category ?? artifact.collection ?? artifact.front_end?.meta?.family ?? 'Other',
      views: artifactViews(artifact),
    }));
  }, [uiConfig.data]);

  const [state, dispatch] = useReducer(reducer, args.page, (page: string | undefined) => ({
    query: { page: pageFromBrowseParam(page) ?? 1, pageSize: 50 },
  }));
  const routedPage = pageFromBrowseParam(args.page);
  const routedPageRef = useRef(routedPage);
  // Back, forward, and a refreshed share update the address bar after mount.
  // A click that has not been written yet leaves this value unchanged, so it
  // does not snap the table back to the previous page.
  useEffect(() => {
    if (routedPage == null || routedPageRef.current === routedPage) return;
    routedPageRef.current = routedPage;
    dispatch({ type: 'page', page: routedPage });
  }, [routedPage]);
  const routeChatId = openChatIdFromRoute(args.threadId, args.artifactType);
  const [openChatId, setOpenChatId] = useState<string | null>(routeChatId);
  // The address bar is the source of truth for back, forward, refresh, and
  // citation links. A click updates openChatId immediately; this follows when
  // the route catches up, and does not clobber a selection the URL has not
  // written yet.
  useEffect(() => {
    setOpenChatId(routeChatId);
  }, [routeChatId]);
  useEffect(() => {
    const view = VIEWS.includes(args.view as UfdrArtifactView)
      ? (args.view as UfdrArtifactView)
      : undefined;
    if (!args.artifactType && !view) return;
    dispatch({ type: 'focus', artifactType: args.artifactType, view });
  }, [args.artifactType, args.view]);

  const artifactType = state.artifactType || undefined;
  const artifact = artifactType ? uiConfig.data?.artifacts[artifactType] : undefined;
  const active = artifactTypes.find((type) => type.id === artifactType);
  const view = state.view && active?.views.includes(state.view) ? state.view : active?.views[0];

  const messageArtifact = preferByCount(messageArtifacts(uiConfig.data), summary.data?.stats);
  const messageView = artifactBubbleView(messageArtifact);
  const sectionThreads = useMessageThreads({
    mobileAcquisitionId: acquisitionId ?? 0,
    view: messageView ?? IDLE_MESSAGE_VIEW,
    artifact: messageArtifact ?? IDLE_ARTIFACT,
    uiConfig: uiConfig.data ?? IDLE_CONFIG,
    active: matched && Boolean(messageArtifact && messageView),
  });

  const openChat = splitUfdrChatId(openChatId);
  const openArtifact = openChat ? uiConfig.data?.artifacts[openChat.graphqlType] : undefined;
  const openView = artifactBubbleView(openArtifact);
  const threadMessages = useThreadMessages({
    mobileAcquisitionId: acquisitionId ?? 0,
    view: openView ?? IDLE_MESSAGE_VIEW,
    artifact: openArtifact ?? IDLE_ARTIFACT,
    detailFields: openView?.detail_fields ?? [],
    threadKey: openChat?.threadKey ?? null,
    active: matched && Boolean(openView),
  });

  const contactArtifact = preferByCount(contactArtifacts(uiConfig.data), summary.data?.stats);
  const contactResolved =
    contactArtifact && uiConfig.data
      ? resolveArtifactColumns(contactArtifact.graphql_type, uiConfig.data)
      : null;
  const contactBuilt =
    contactResolved && uiConfig.data && contactArtifact && acquisitionId != null
      ? buildArtifactListQuery(contactResolved, contactArtifact.endpoint, uiConfig.data, {
          mobileAcquisitionId: acquisitionId,
          offset: 0,
          limit: 200,
        })
      : null;
  const contactList = useArtifactList({
    mobileAcquisitionId: acquisitionId ?? 0,
    graphqlType: contactArtifact?.graphql_type ?? '',
    endpoint: contactBuilt?.endpoint ?? 'mobile',
    rootQuery: contactBuilt?.rootQuery ?? '',
    query: matched && contactBuilt ? contactBuilt.query : null,
    keyParams: { view: 'contacts' },
  });

  const sectionTimeline = useTimelineEvents({
    mobileAcquisitionId: acquisitionId ?? 0,
    uiConfig: uiConfig.data,
    direction: 'desc',
    offset: 0,
    limit: 200,
    where: null,
    active: matched && args.section === 'timeline',
  });

  const workspace = useMobileForensicWorkspace({
    matched,
    acquisitionId,
    uiConfig: uiConfig.data,
    artifact,
    artifactType,
    view,
    section: args.section ?? 'overview',
    sectionGraphqlType: messageArtifact?.graphql_type,
    highlightIds: args.rowIds,
    legendLabel: t('viewers.map'),
    partialNotice: t('mobileForensic.mapPartial'),
    openChatId,
    routeFilters: args.filters,
    routeSearch: args.search,
    onSearchHit: (nextType) => dispatch({ type: 'artifact', artifactType: nextType }),
  });

  const resolved = artifactType && uiConfig.data
    ? resolveArtifactColumns(artifactType, uiConfig.data)
    : null;
  const where = compileWhere(workspace.filters, workspace.allowlist);
  const sortColumn = resolved?.columns.find((column) => column.field === state.query.sort?.columnId);
  const orderBy =
    state.query.sort && sortColumn?.sortable
      ? buildOrderBy({ field: state.query.sort.columnId, direction: state.query.sort.direction })
      : null;
  const built = resolved && uiConfig.data && artifact && acquisitionId != null
    ? buildArtifactListQuery(resolved, artifact.endpoint, uiConfig.data, {
        mobileAcquisitionId: acquisitionId,
        offset: (state.query.page - 1) * state.query.pageSize,
        limit: state.query.pageSize,
        where,
        orderBy,
      })
    : null;
  const list = useArtifactList({
    mobileAcquisitionId: acquisitionId ?? 0,
    graphqlType: artifactType ?? '',
    endpoint: built?.endpoint ?? 'mobile',
    rootQuery: built?.rootQuery ?? '',
    query: matched && built ? built.query : null,
    keyParams: { ...state.query, where },
  });

  const table: UfdrTableModel | undefined = resolved && list.data
    ? {
        columns: workspace.columns ?? resolved.columns.map((column) => ({
          id: column.field,
          label: column.label,
        })),
        rows: list.data.items.map((row, index) => {
          const id = String(row.mobileArtifactId ?? row.id ?? index);
          const cells: Record<string, string> = {};
          for (const column of resolved.columns) {
            const value = row[column.field];
            cells[column.field] = value == null ? '' : typeof value === 'string' ? value : JSON.stringify(value);
          }
          return { id, cells };
        }),
        total: list.data.total,
        query: state.query,
        highlightIds: args.rowIds,
        selectedId: undefined,
      }
    : undefined;

  const sectionChats = messageArtifact
    ? mapThreadsToChats(
        sectionThreads.data ?? [],
        messageArtifact.title || messageArtifact.graphql_type,
        messageArtifact.graphql_type,
      )
    : [];
  const chatsWithMessages = attachChatMessages(
    sectionChats,
    openChatId,
    threadMessages.data ? mapMappedMessages(threadMessages.data) : undefined,
  );
  const workspaceChats = attachChatMessages(
    workspace.workspaceChats,
    openChatId,
    threadMessages.data ? mapMappedMessages(threadMessages.data) : undefined,
  );
  const mobileContacts = mapContactRows(contactList.data?.items ?? []);
  const mobileTimeline =
    args.section === 'timeline' ? mapMobileTimeline(sectionTimeline.data?.events ?? []) : undefined;

  const ownerRows = (owners.data?.get(acquisitionId ?? -1) ?? []).map((owner) => ({
    id: String(owner.mobileArtifactId),
    displayName: owner.displayName ?? 'Unknown owner',
    summary: owner.llmOwner ?? undefined,
    phoneNumbers: owner.phoneNumbers,
    emails: owner.emails,
    confidenceTier: owner.confidenceTier,
  }));

  const data = {
    ...(args.sidecar ?? { tree: [] }),
    title: args.sidecar?.title || acquisition.caseName || undefined,
    chats: chatsWithMessages && chatsWithMessages.length > 0 ? chatsWithMessages : args.sidecar?.chats,
    contacts: (() => {
      const linked = withChatIds(
        mobileContacts.length > 0 ? mobileContacts : (args.sidecar?.contacts ?? []),
        chatsWithMessages ?? [],
      );
      return linked.length > 0 ? linked : undefined;
    })(),
    timeline: mobileTimeline && mobileTimeline.length > 0 ? mobileTimeline : args.sidecar?.timeline,
    stats: summary.data ? mapAcquisitionStats(summary.data.stats, uiConfig.data) : args.sidecar?.stats,
    owners: ownerRows.length ? ownerRows : args.sidecar?.owners,
    artifactTypes: artifactTypes.length ? artifactTypes : undefined,
  } as UfdrExportData;

  return {
    matched,
    loading: acquisition.loading || (matched && (uiConfig.isLoading || list.isLoading || summary.isLoading)),
    error: list.error?.message ?? uiConfig.error?.message ?? acquisition.error ?? null,
    data,
    artifactType,
    view,
    table,
    workspace: {
      table,
      chats: workspaceChats,
      messagePage: {
        loading: threadMessages.isFetching,
        hasMore: false,
        threadId: openChatId ?? undefined,
      },
      mapData: workspace.mapData,
      mapPartialNotice: workspace.mapPartialNotice,
      highlightMarkerIds: workspace.highlightMarkerIds,
      gallery: workspace.gallery,
      cards: workspace.cards,
      timeline: workspace.timeline,
      timelineHasMore: workspace.timelineHasMore,
      facets: workspace.facets,
      dateExtent: workspace.dateExtent,
      dateRange: workspace.dateRange,
      onFacetToggle: workspace.onFacetToggle,
      onDateRangeChange: workspace.onDateRangeChange,
      query: workspace.query,
      onQueryChangeText: workspace.onQueryChangeText,
      searchResults: workspace.searchResults,
      onSearchHit: workspace.onSearchHit,
      record: workspace.record,
      dock: undefined,
      dockPanel: workspace.dockPanel,
      onDockPanelChange: workspace.onDockPanelChange,
    },
    onArtifactTypeChange: (next: string) => dispatch({ type: 'artifact', artifactType: next }),
    onViewChange: (next: UfdrArtifactView) => dispatch({ type: 'view', view: next }),
    onQueryChange: (query: UfdrQuery) => {
      dispatch({ type: 'query', query });
      workspace.applyQueryFilters(query);
    },
    onChatSelect: (chat: { id: string } | undefined) => setOpenChatId(chat?.id ?? null),
    onRowSelect: workspace.onRowSelect,
    acquisitionId: acquisitionId ?? undefined,
    caseName: acquisition.caseName || data.title,
    uiConfig: uiConfig.data,
    rows: list.data?.items ?? [],
    total: list.data?.total ?? 0,
    page: state.query.page,
    pageSize: state.query.pageSize,
    openChatId,
  };
}
